import { useEffect, useState } from 'react';
import type { Machine, MachineStatus } from '@bridgewerk/shared';
import { StatusDot } from '../../../components/layout/StatusDot.js';
import { Badge } from '../../../components/ui/Badge.js';

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

function useRelativeTime(isoTimestamp: string): string {
  const compute = () => {
    const diff = Date.now() - new Date(isoTimestamp).getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  };
  const [label, setLabel] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setLabel(compute()), 30_000);
    return () => clearInterval(id);
  }, [isoTimestamp]);
  return label;
}

const STATUS_LABEL: Record<MachineStatus, string> = {
  running: 'RUNNING',
  idle: 'IDLE',
  alarm: 'ALARM',
  offline: 'OFFLINE',
};

const STATUS_COLOR: Record<MachineStatus, string> = {
  running: 'var(--color-green)',
  idle: 'var(--color-yellow)',
  alarm: 'var(--color-red)',
  offline: 'var(--color-muted)',
};

const CONTROLLER_LABEL: Record<string, string> = {
  fanuc_0i_md: 'Fanuc 0i-MD',
  fanuc_30i: 'Fanuc 30i',
  mazak_matrix: 'Mazak Matrix',
  mazak_smoothx: 'Mazak SmoothX',
  haas_nextgen: 'Haas NextGen',
  mori_mapps: 'Mori MAPPS',
  okuma_osp: 'Okuma OSP',
  siemens_840d: 'Siemens 840D',
  heidenhain_tnc: 'Heidenhain TNC',
  unknown: 'Unknown',
};

export interface MachineCardProps {
  readonly machine: Machine;
  readonly insight?: string;
  readonly onClick?: () => void;
}

export function MachineCard({ machine, insight, onClick }: MachineCardProps): JSX.Element {
  const status = machine.status;
  const color = STATUS_COLOR[status];
  const isAlarm = status === 'alarm';
  const interactive = onClick != null;
  const lastSeen = useRelativeTime(machine.last_seen_at);
  const isStale = status !== 'offline' &&
    Date.now() - new Date(machine.last_seen_at).getTime() > STALE_THRESHOLD_MS;

  return (
    <article
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={[
        'relative rounded-md bg-card border border-border overflow-hidden',
        'transition-colors',
        interactive ? 'cursor-pointer hover:border-border-2 focus:outline-none focus:border-accent' : '',
      ].join(' ')}
      style={{
        borderLeft: `3px solid ${color}`,
        animation: isAlarm ? 'pulse-glow 1.6s ease-in-out infinite' : undefined,
        color: isAlarm ? color : undefined,
      }}
      aria-label={`Machine ${machine.machine_id}, status ${status}`}
    >
      <div className="p-4 space-y-3" style={{ color: 'var(--color-text)' }}>
        <header className="flex items-center justify-between gap-2">
          <h3 className="font-mono text-sm">{machine.machine_id}</h3>
          <Badge tone={statusTone(status)}>
            <StatusDot status={status} size={6} />
            <span className="ml-1.5">{STATUS_LABEL[status]}</span>
          </Badge>
        </header>

        <div className="text-[11px] text-text-dim font-mono">
          {CONTROLLER_LABEL[machine.controller_type] ?? machine.controller_type}
          {machine.program_name && (
            <>
              <span className="mx-2 text-muted">·</span>
              <span>{machine.program_name}</span>
            </>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Metric label="SPINDLE" value={`${formatNumber(machine.spindle_rpm)}`} unit="rpm" />
          <Metric label="FEED" value={`${formatNumber(machine.feed_rate)}`} unit="mm/min" />
          {status === 'alarm' && machine.alarm_code ? (
            <Metric label="ALARM CODE" value={machine.alarm_code} valueColor="var(--color-red)" />
          ) : (
            <Metric
              label="OEE"
              value={`${Math.round(machine.oee_percent)}`}
              unit="%"
              valueColor={oeeColor(machine.oee_percent)}
            />
          )}
        </div>

        <UtilizationBar percent={machine.oee_percent} color={color} />

        {isStale && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-orange">
            <span>⚠</span>
            <span>Data stale · last seen {lastSeen}</span>
          </div>
        )}

        {!isStale && status === 'offline' && (
          <p className="text-[10px] font-mono text-muted">Last seen {lastSeen}</p>
        )}

        {insight && (
          <div
            className="text-[12px] leading-snug rounded-md p-2"
            style={{
              backgroundColor: 'rgba(0,196,232,0.06)',
              border: '1px solid rgba(0,196,232,0.15)',
            }}
          >
            <span className="text-accent font-semibold">⚡ WerkOS:</span>{' '}
            <span className="text-text">{insight}</span>
          </div>
        )}
      </div>
    </article>
  );
}

function Metric({
  label,
  value,
  unit,
  valueColor,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly valueColor?: string;
}): JSX.Element {
  return (
    <div>
      <div className="font-mono text-[8px] uppercase tracking-[0.25em] text-text-dim">{label}</div>
      <div className="mt-1 font-mono text-base" style={{ color: valueColor ?? 'var(--color-text)' }}>
        {value}
        {unit && <span className="ml-1 text-[10px] text-text-dim">{unit}</span>}
      </div>
    </div>
  );
}

function UtilizationBar({ percent, color }: { readonly percent: number; readonly color: string }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className="h-1 rounded-full overflow-hidden"
      style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
      aria-label={`utilization ${clamped.toFixed(0)} percent`}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full transition-[width] duration-700 ease-out"
        style={{ width: `${clamped}%`, backgroundColor: color }}
      />
    </div>
  );
}

function statusTone(status: MachineStatus): 'opportunity' | 'warning' | 'critical' | 'neutral' {
  switch (status) {
    case 'running':
      return 'opportunity';
    case 'idle':
      return 'warning';
    case 'alarm':
      return 'critical';
    case 'offline':
      return 'neutral';
  }
}

function oeeColor(pct: number): string {
  if (pct >= 70) return 'var(--color-green)';
  if (pct >= 50) return 'var(--color-yellow)';
  return 'var(--color-red)';
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toLocaleString();
  return String(Math.round(n));
}
